import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { waeAPI, fmtDuration } from "./waeApi";
import CountUp from "@/pages/operations/CountUp";
import {
  GitBranch, Clock, CheckCircle2, XCircle, Timer, ArrowUpCircle,
  UserCheck, AlertTriangle, TrendingUp, ArrowRight, Send, Inbox, Layers,
} from "lucide-react";

const Tile = ({ label, value, icon: Icon, accent = "text-hocp-gold", sub, testid }) => (
  <div data-testid={testid}
    className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
    <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
    <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent} relative`}>
      <Icon className="w-5 h-5" strokeWidth={1.75}/>
    </div>
    <div className="mt-4 relative">
      <div className="font-display text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
      <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[10.5px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

export default function WAEDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => waeAPI.dashboard().then(setD).catch(()=>{});
    load(); const iv = setInterval(load, 20000); return () => clearInterval(iv);
  }, []);
  const k = d?.kpis || {}; const dept = d?.departments || []; const trend = d?.trend || [];
  const types = d?.approval_types || [];
  const maxT = Math.max(1, ...trend.map(t => (t.approved||0)+(t.rejected||0)+(t.pending||0)));

  return (
    <div className="space-y-5" data-testid="wae-dashboard">
      {/* Hero */}
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
          <GitBranch className="w-3.5 h-3.5"/> Workflow Mission Control
          <span className="text-hocp-gold font-semibold tabular-nums">{d?.business_date || "—"}</span>
        </div>
        <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none">
          <CountUp value={Number(k.pending) || 0}/>
          <span className="text-slate-400 text-[22px]"> awaiting action</span>
        </div>
        <div className="text-[13px] text-slate-300 mt-2">
          {k.approved_today || 0} approved · {k.rejected_today || 0} rejected · avg {fmtDuration(k.avg_approval_secs)}
        </div>
        {/* Quick actions */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="wae-quick-actions">
          <Link to="/workflow/inbox" data-testid="wae-qa-inbox"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[12.5px] font-semibold hover:brightness-95 transition">
            <Inbox className="w-4 h-4"/> Open My Inbox
          </Link>
          <Link to="/workflow/my-requests?new=1" data-testid="wae-qa-new"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white/10 text-white text-[12.5px] font-medium hover:bg-white/15 border border-white/10 transition">
            <Send className="w-4 h-4"/> New Request
          </Link>
          <Link to="/workflow/designer" data-testid="wae-qa-designer"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white/10 text-white text-[12.5px] font-medium hover:bg-white/15 border border-white/10 transition">
            <Layers className="w-4 h-4"/> Design a Workflow
          </Link>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Tile label="Pending Approvals"    value={<CountUp value={Number(k.pending)||0}/>}         icon={Clock}         testid="wae-kpi-pending"    accent="text-sky-300"/>
        <Tile label="Approved Today"       value={<CountUp value={Number(k.approved_today)||0}/>}  icon={CheckCircle2}  testid="wae-kpi-approved"   accent="text-emerald-300"/>
        <Tile label="Rejected Today"       value={<CountUp value={Number(k.rejected_today)||0}/>}  icon={XCircle}       testid="wae-kpi-rejected"   accent="text-rose-300"/>
        <Tile label="Avg Approval Time"    value={fmtDuration(k.avg_approval_secs)}                icon={Timer}         testid="wae-kpi-avg"        accent="text-fuchsia-300"/>
        <Tile label="Escalated"            value={<CountUp value={Number(k.escalated)||0}/>}       icon={ArrowUpCircle} testid="wae-kpi-escalated"  accent="text-amber-300"/>
        <Tile label="Delegated"            value={<CountUp value={Number(k.delegated)||0}/>}       icon={UserCheck}     testid="wae-kpi-delegated"  accent="text-cyan-300"/>
        <Tile label="SLA Breaches"         value={<CountUp value={Number(k.sla_breaches)||0}/>}    icon={AlertTriangle} testid="wae-kpi-sla"        accent={Number(k.sla_breaches)>0?"text-rose-300":"text-slate-300"}/>
        <Tile label="Success Rate (30d)"   value={`${k.success_rate || 0}%`}                        icon={TrendingUp}    testid="wae-kpi-success"    accent="text-hocp-gold"/>
      </div>

      {/* Trend + Approval Type breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="wae-trend">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-hocp-navy"/>
            <div className="font-display text-[14px] font-semibold text-hocp-navy">14-Day Approval Trend</div>
          </div>
          <div className="p-4">
            <div className="flex items-end gap-1.5 h-40">
              {trend.map(t => {
                const total = (t.approved||0)+(t.rejected||0)+(t.pending||0);
                return (
                  <div key={t.date} className="flex-1 flex flex-col items-center justify-end gap-1"
                       title={`${t.date}: ${t.approved} approved, ${t.rejected} rejected, ~${t.pending}`}>
                    <div className="w-full flex flex-col justify-end" style={{ height: `${(total/maxT)*100}%` }}>
                      {t.pending > 0 && <div className="w-full bg-sky-300" style={{ height: `${(t.pending/Math.max(total,1))*100}%` }}/>}
                      {t.rejected > 0 && <div className="w-full bg-rose-300" style={{ height: `${(t.rejected/Math.max(total,1))*100}%` }}/>}
                      {t.approved > 0 && <div className="w-full bg-emerald-400" style={{ height: `${(t.approved/Math.max(total,1))*100}%` }}/>}
                    </div>
                    <div className="text-[9px] text-slate-400 tabular-nums">{t.date.slice(-2)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-emerald-400"/>Approved</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-rose-300"/>Rejected</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-sky-300"/>Pending</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="wae-types">
          <div className="px-4 py-3 border-b border-slate-100 font-display text-[14px] font-semibold text-hocp-navy">
            Approval Types (30d)
          </div>
          {types.length === 0
            ? <div className="p-6 text-center text-[12.5px] text-slate-400">No requests yet.</div>
            : (
              <div className="divide-y divide-slate-100">
                {types.slice(0,8).map(t => {
                  const max = Math.max(1, ...types.map(x=>x.count));
                  const pct = Math.max(6, Math.round((t.count/max)*100));
                  const label = t.code.replace(/^wae\./, "").replace(/_/g, " ");
                  return (
                    <div key={t.code} className="px-4 py-2.5" data-testid={`wae-type-${t.code}`}>
                      <div className="flex items-center justify-between text-[12.5px]">
                        <span className="text-hocp-navy capitalize truncate">{label}</span>
                        <span className="mono tabular-nums text-slate-500">{t.count}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-hocp-gold" style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="wae-departments">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="font-display text-[14px] font-semibold text-hocp-navy">Department Breakdown (30 days)</div>
          <Link to="/workflow/history" className="ml-auto inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-hocp-navy">
            View history <ArrowRight className="w-3.5 h-3.5"/>
          </Link>
        </div>
        {dept.length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No department activity yet.</div>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Department</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3">Approved</th>
                  <th className="text-right px-4 py-3">Rejected</th>
                  <th className="text-right px-4 py-3">Pending</th>
                  <th className="text-left px-4 py-3">Success</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dept.map(d => {
                  const rate = d.count > 0 ? Math.round((d.approved / d.count) * 100) : 0;
                  return (
                    <tr key={d.department_id} data-testid={`wae-dept-${d.department_id}`} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium text-hocp-navy">{d.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums mono">{d.count}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-600 font-medium">{d.approved}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-rose-600">{d.rejected}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-sky-600">{d.pending}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 max-w-[120px] overflow-hidden">
                            <div className="h-full bg-emerald-400" style={{ width: `${rate}%` }}/>
                          </div>
                          <span className="text-[11.5px] tabular-nums text-slate-500 w-8 text-right">{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
